import React from 'react'
import { usePublicSiteBranding, usePublicSiteId } from '../../contexts/PublicSiteContext'
import type { PublicSiteId } from '../../utils/publicSite'

interface WhyFeature {
  title: string
  description: string
}

const storageWalkFeatures: WhyFeature[] = [
    {
        title: 'Effortless Monitoring',
        description: 'Use our intuitive app to conduct and record your morning and evening facility walks. No more cumbersome paper trails or disjointed notes; everything you need is in one place.'
    },
    {
        title: 'Real-Time GPS Tracking',
        description: 'Our state-of-the-art GPS technology combined with seamless AI tracking confirms your walks, ensuring accountability and providing an accurate map of covered areas.'
    },
    {
        title: 'AI-Assisted Issue Detection',
        description: 'Identify potential issues like missing locks or maintenance needs with AI assistance. Our technology helps in recognizing irregularities, ensuring nothing slips through the cracks.'
    },
    {
        title: 'Photo Documentation',
        description: 'Snap pictures of any irregularities or points of interest. Each photo is geo-tagged and time-stamped, providing visual evidence and context to any reported issue.'
    },
    {
        title: 'Instant Reporting',
        description: 'All data from walks, including notes and photos, instantly syncs to our secure cloud. Facility owners can access real-time reports, review progress, and address issues promptly from anywhere in the world.'
    },
    {
        title: 'Custom Alerts',
        description: "Set up custom notifications for specific issues or when walks are missed, ensuring that you're always in the loop."
    }
]

const patrolWalkFeatures: WhyFeature[] = [
    {
        title: 'Verified Patrol Rounds',
        description: 'GPS-verified patrol routes prove exactly when and where your officers walked. No more uncertainty about whether rounds were actually completed.'
    },
    {
        title: 'Real-Time GPS Tracking',
        description: 'Live location tracking shows patrol progress as it happens. Operations can see coverage in real time and respond to gaps immediately.'
    },
    {
        title: 'Incident Documentation',
        description: 'Officers document incidents with photos, notes, and precise timestamps. Every observation becomes part of a defensible record.'
    },
    {
        title: 'Photo Checkpoints',
        description: 'Required checkpoint photos prove presence at key locations. Each image is geo-tagged and time-stamped for accountability.'
    },
    {
        title: 'Instant Reporting',
        description: 'All patrol data syncs to the cloud in real time. Supervisors and clients can review completed rounds and incidents from anywhere.'
    },
    {
        title: 'Missed Round Alerts',
        description: "Automatic notifications when patrols aren't completed on schedule. Never wonder if a round was skipped."
    }
]

const constructionWalkFeatures: WhyFeature[] = [
    {
        title: 'One Mobile Workflow',
        description: 'Employees, foremen, superintendents, operations and payroll all work from the same platform. No separate apps, no duplicate entry.'
    },
    {
        title: 'Accounting Integration',
        description: 'Approved field time posts directly into Sage 100 Contractor or QuickBooks Online without rekeying. Your accounting system stays the source of truth; ConstructionWalk captures what actually happened.'
    },
    {
        title: 'Defensible Daily Record',
        description: 'Time-stamped documentation for disputes, changed conditions, safety incidents and progress. Every walk creates an auditable record of the day.'
    },
    {
        title: 'Photo-Verified Job Walks',
        description: 'Structured job walks with required checkpoint photos. GPS and timestamps prove when inspections occurred and what was documented.'
    },
    {
        title: 'Exception-Driven Payroll',
        description: 'Payroll reviews exceptions instead of inspecting every timecard. Focus on corrections, not routine approvals.'
    },
    {
        title: 'Daily Field Reports',
        description: 'Supervisor daily reports capture weather, people on site, meetings, equipment and notes. The complete daily operating record in one place.'
    }
]

function getWhyFeatures(siteId: PublicSiteId): WhyFeature[] {
  switch (siteId) {
    case 'security-walk':
      return patrolWalkFeatures
    case 'construction-walk':
      return constructionWalkFeatures
    default:
      return storageWalkFeatures
  }
}

const WhyStorageWalk = ({whyStorageWalk}: any) => {
  const branding = usePublicSiteBranding()
  const siteId = usePublicSiteId()
  const features = getWhyFeatures(siteId)

  return (
    <div ref={whyStorageWalk} className="w-full mx-auto py-20 px-6 bg-marketing-muted">
        <div className="flex flex-col">
            <div className="flex flex-col w-full">
                <div className="text-3xl font-bold lg:text-4xl flex justify-center py-4 text-marketing-ink">
                    {branding.whySectionHeading}
                </div>
                <div className='flex flex-row flex-wrap justify-center'>
                    {features.map((item, index) => (
                        <div key={index} className="flex flex-col text-center p-5 sm:w-1/2 lg:w-1/4">
                            <div className='w-full flex  items-center h-56 sm:h-64 md:h-72 rounded-md p-3 flex-col bg-marketing-accent' >
                                    <div className='text-white text-lg font-semibold flex pt-5'>
                                        {item.title}
                                    </div>
                                    <div className='flex h-full items-center'>
                                        <p className='text-white text-sm px-2'>
                                            {item.description}
                                        </p>
                                    </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
  )
}

export default WhyStorageWalk
